// Facebook authentication helper — full-page OAuth redirect flow (no FB JS SDK).
// The FB JS SDK popup returns status "unknown" under third-party-cookie partitioning
// (Safari ITP, Brave, Chrome incognito). A full-page redirect does not rely on
// third-party cookies, so it works reliably across all modern browsers.
import { toast } from "sonner";
import type { SignupContext } from "@shared/onboarding";

const GRAPH_API_VERSION = "v23.0";

const generateNonce = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export function startFacebookLogin(signupContext?: SignupContext): void {
  if (typeof window === "undefined") return;

  const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
  if (!appId) {
    toast.error("خطأ في التكوين", {
      description: "لم يتم تكوين مفتاح Facebook بشكل صحيح",
    });
    return;
  }

  // CSRF nonce echoed back by the backend in the redirect hash and verified on return.
  // localStorage, NOT sessionStorage: on mobile the OAuth round-trip can app-switch
  // into the native Facebook app and come back in a NEW browser tab, where a per-tab
  // sessionStorage is empty — the nonce check would fail and the token get discarded.
  const nonce = generateNonce();
  localStorage.setItem("fb_oauth_state", nonce);
  // Belt-and-braces: also mirror to sessionStorage. The verifier accepts either,
  // so a cached pre-localStorage page on one side of the round-trip can't strand
  // the nonce (mobile browsers hold old tabs/bundles for a long time).
  sessionStorage.setItem("fb_oauth_state", nonce);

  let returnTo = window.location.pathname + window.location.search;
  if (!returnTo.startsWith("/")) returnTo = "/" + returnTo;

  const redirectUri =
    new URL(import.meta.env.VITE_API_URL).origin + "/api/auth/facebook/callback";
  // `o` = the origin the flow started on. Both apex and www serve the site, but
  // localStorage is per-origin — if the backend bounced everyone to FRONTEND_URL,
  // a login started on the other origin would never find its nonce. The backend
  // validates `o` against a strict allowlist before using it.
  // `c` = the signup context (Phase 49, D-04), short keys to keep the URL small: t = trigger,
  // s = storeSlug, v = lastViewedStoreSlug. Advisory only: the backend allowlists it, stores it on
  // a new account, and never uses it for the redirect.
  const c = signupContext
    ? {
        t: signupContext.trigger,
        ...(signupContext.storeSlug ? { s: signupContext.storeSlug } : {}),
        ...(signupContext.lastViewedStoreSlug ? { v: signupContext.lastViewedStoreSlug } : {}),
      }
    : undefined;
  const state = encodeURIComponent(
    JSON.stringify({ n: nonce, r: returnTo, o: window.location.origin, ...(c ? { c } : {}) })
  );

  window.location.assign(
    `https://www.facebook.com/${GRAPH_API_VERSION}/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&scope=email,public_profile&response_type=code&state=${state}`
  );
}
